import React, { useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import * as AppPics from 'assets/images';
import TablePaginate from 'components/Paginate';
import usePaginateDefaults from 'hooks/usePaginateDefaults';
import BasicDelete from 'components/molecules/Modal/Delete/BasicDelete';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { ArrowsPng } from 'assets/images';
import usePodcasts from 'hooks/API/GET/usePodcasts';
import useDeletePodcast from 'hooks/API/DELETE/useDeletePodcast';
import { notifier } from 'components/Toaster';
import { AppRadioBox, AppTextField } from 'components';
import useDebounce from 'hooks/useDebounce';
import useCategoryDropdown from 'hooks/API/GET/useCategoryDropdown';
import { useMemo } from 'react';
import PodcastCategoryDropdown from 'components/molecules/PodcastCategoryDropDown';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';

const initialPodcastState = {
  podcastName: '',
  podcastSubtitle: '',
  podcastArtist: '',
  podcastDuration: '',
  podcastUrl: null,
  section: '',
  podcastAlbumImage: '',
  podcastAlbumImageFullPath: '',
  podcastImage: '',
  podcastImageFullPath: '',
  podcastCategory: '',
};

function PodcastList() {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();

  const urlParams = new URLSearchParams(location.search);
  const initialKeyword = urlParams.get('keyword') || '';
  const initialCategory = urlParams.get('category') || '';
  const initialSection = urlParams.get('section') || '';
  const initialPage = parseInt(urlParams.get('page')) || 0;

  const [podcastInfo, setPodcastInfo] = useState({
    ...initialPodcastState,
    section: initialSection,
  });
  const [params, setParams] = usePaginateDefaults({ limit: 5, page: initialPage });
  const [enableFilter, setEnableFilter] = useState(false);

  const [searchTerm, setSearchTerm] = useState(initialKeyword);
  const searchKeyword = useDebounce(searchTerm, 1000);

  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [categoriesList, setCategoriesList] = useState([]);

  const actionsConfig = {
    open: false,
    data: {},
    Header: null,
    Body: null,
    isDeleteOperation: false,
  };
  const [actions, setActions] = useState({ ...actionsConfig });

  const updateURL = (newParams) => {
    const currentParams = new URLSearchParams(location.search);

    Object.entries(newParams).forEach(([key, value]) => {
      if (value && typeof value === 'string' && value.trim() !== '') {
        currentParams.set(key, value);
      } else {
        currentParams.delete(key);
      }
    });

    const newSearch = currentParams.toString();
    const newURL = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;

    if (newURL !== location.pathname + location.search) {
      history.replace(newURL);
    }
  };

  useEffect(() => {
    updateURL({
      keyword: searchKeyword,
      category: selectedCategory,
      section: podcastInfo.section,
      page: params.page,
    });
  }, [searchKeyword, selectedCategory, podcastInfo.section, params.page]);

  useEffect(() => {
    const updatedParams = new URLSearchParams(location.search);
    const newKeyword = updatedParams.get('keyword') || '';
    const newCategory = updatedParams.get('category') || '';
    const newSection = updatedParams.get('section') || '';
    const newPage = parseInt(updatedParams.get('page')) || 0;

    setSearchTerm(newKeyword);
    setSelectedCategory(newCategory);
    setPodcastInfo((prev) => ({ ...prev, section: newSection }));
    setParams((prev) => ({ ...prev, page: newPage }));
  }, [location.key]);

  const podcastParams = useMemo(() => {
    return {
      ...params,
      keyword: searchKeyword,
      podcastCategory: selectedCategory || undefined,
      ...(podcastInfo.section && { section: podcastInfo.section }),
    };
  }, [params, searchKeyword, selectedCategory, podcastInfo.section]);

  const { data, isLoading } = usePodcasts({
    enabled: true,
    configs: { params: podcastParams },
    queryKey: ['podcasts', podcastParams],
  });

  const {
    data: categoryData,
    isLoading: isCategoryLoading,
    isError: isCategoryError,
  } = useCategoryDropdown({ enabled: true });

  const { mutate: deletePodcast } = useDeletePodcast();

  // const podcasts = data?.data?.data?.result || [];
  const podcasts = [...(data?.data?.data?.result || [])].sort(
    (a, b) => (a.sortPosition || 0) - (b.sortPosition || 0)
  );

  const { meta: { page, total } = {} } = data?.data?.data ?? {};

  useEffect(() => {
    if (!isCategoryLoading && Array.isArray(categoryData?.data)) {
      const categories = categoryData.data.map((item) => ({
        value: item.value,
        label: item.label,
      }));
      setCategoriesList(categories);
    }
  }, [categoryData, isCategoryLoading]);

  const handleDelete = (podcast) => {
    setActions({
      open: true,
      data: { id: podcast._id },
      Header: 'Delete Podcast',
      Body: `Are you sure you want to delete "${podcast.name}"?`,
      isDeleteOperation: true,
    });
  };

  const handleDeleteConfirm = () => {
    deletePodcast(actions.data.id, {
      onSuccess: (res) => {
        notifier.success(res?.data?.data?.message);
        queryClient.invalidateQueries('podcasts');
      },
      onError: (err) => {
        notifier.error(err?.response?.data?.data?.message);
      },
    });
    setActions({ ...actionsConfig });
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setParams((prev) => ({ ...prev, page: 0 }));
  };

  const handleSectionChange = (value) => {
    setPodcastInfo((prev) => ({ ...prev, section: value }));
    setParams((prev) => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setParams((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (limit) => {
    setParams((prev) => ({ ...prev, page: 0, limit }));
  };

  const clearAllFilters = () => {
    setPodcastInfo({ ...initialPodcastState });
    setSelectedCategory('');
    setSearchTerm('');
    setParams((prev) => ({ ...prev, page: 0 }));
    history.replace(location.pathname);
  };

  return (
    <>
      <BasicDelete
        open={actions.open}
        closeModal={() => setActions({ ...actionsConfig })}
        Header={actions.Header}
        Body={actions.Body}
        handleConfirmation={handleDeleteConfirm}
      />

      <div className="mainWraper coursetabsection">
        <div className="tab">
          <ul className="tab__menu">
            <li className="back">
              <Link to="/podcast">
                <button className="btn-back">
                  <img src={AppPics.BackSvg} alt="Back" />
                </button>
              </Link>
            </li>
          </ul>

          <div className="tab__content ___adjust___tab___padding">
            <div className="tab__content-actHead">
              <AppBreadcrumbs
                links={[
                  { label: 'Dashboard', to: '/dashboard' },
                  { label: 'Podcast Home', to: '/podcast' },
                ]}
                current="Podcasts"
              />
            </div>
            <div className="tab__content-actBody">
              <div className="px-5 py-5">
                <div className="tab__contentMenu">
                  <ul className="tab__contentMenu-list">
                    <li>
                      <h2 className="prime-head p-0 boldtitle text-nowrap">Manage Podcast</h2>
                    </li>
                  </ul>

                  <div className="mainHeader__search">
                    <ul className="tab__contentMenu-list">
                      <li className="relative">
                        <input
                          className="mainHeader__searchInput"
                          placeholder="Search podcast..."
                          type="text"
                          value={searchTerm}
                          onChange={(event) => setSearchTerm(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                            }
                          }}
                        />
                        <span className="mainHeader__searchBtn">
                          <img src={AppPics.TopMenuSearchSvg} alt="Search" />
                        </span>
                      </li>
                      <li className="px-4 flex items-center">
                        <button
                          type="button"
                          onClick={() => setEnableFilter(!enableFilter)}
                          className="flex items-center gap-1"
                        >
                          <img src={AppPics.TabFilterSvg} alt="List filter" />
                          <span className="viewsettingtitle">Filter</span>
                        </button>
                      </li>
                    </ul>
                  </div>

                  <div className="flex items-center gap-10">
                    {!enableFilter && !selectedCategory && !searchKeyword && (
                      <Link to="/podcast/reorder" className="mr-4">
                        <img
                          src={ArrowsPng}
                          alt="Reorder Podcast"
                          title="Reorder Podcast"
                          className="cursor-pointer mr-6"
                        />
                      </Link>
                    )}
                    <Link to="/podcast/add">
                      <button className="btn-left-content add">New Podcast</button>
                    </Link>
                  </div>
                </div>
                <div className="tab__content-actBody">
                  {enableFilter && (
                    <div className="row mt-3">
                      <div className="col-lg-3">
                        <label className="label d-block">Podcast Section</label>
                        <div className="radio-group">
                          <AppRadioBox
                            inputProps={{
                              name: 'section',
                              value: 'trending',
                              checked: podcastInfo.section === 'trending',
                              onChange: () => handleSectionChange('trending'),
                            }}
                            labelProps={{ label: 'Trending' }}
                          />
                          <AppRadioBox
                            inputProps={{
                              name: 'section',
                              value: 'popular',
                              checked: podcastInfo.section === 'popular',
                              onChange: () => handleSectionChange('popular'),
                            }}
                            labelProps={{ label: 'Popular' }}
                          />
                        </div>
                      </div>

                      <div className="col-lg-3">
                        <PodcastCategoryDropdown
                          value={selectedCategory}
                          handleChange={(e) => handleCategoryChange(e.target.value)}
                          options={categoriesList}
                          isLoading={isCategoryLoading}
                          isError={isCategoryError}
                          version="v2"
                          defaultLook={false}
                        />
                      </div>

                      <div className="col-lg-3 d-flex">
                        <button
                          type="button"
                          className="btn btn-plane p--tint"
                          style={{ alignSelf: 'end' }}
                          onClick={clearAllFilters}
                        >
                          Clear filter
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="tab__contentBody">
                    <div className="tableWrap">
                      <div className="table-responsive">
                        <table className="table">
                          <thead className="thead-light __appTheadColor">
                            <tr>
                              <th className="text-center">#</th>
                              <th className="text-center">Podcast</th>
                              <th className="text-center">Subtitle</th>
                              <th className="text-center">Artists</th>
                              <th className="text-center">Genre</th>
                              <th className="text-center">Status</th>
                              <th className="text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {isLoading ? (
                              <tr>
                                <td colSpan={6} className="text-center py-4">
                                  Loading podcasts...
                                </td>
                              </tr>
                            ) : podcasts.length > 0 ? (
                              podcasts.map((podcast, idx) => (
                                <tr key={podcast._id}>
                                  <td className="text-center">
                                    {params.page * params.limit + idx + 1}
                                  </td>
                                  <td className="text-center">
                                    {podcast.name}
                                    <p>
                                      <small className="text-muted">
                                        Duration:{' '}
                                        {podcast.duration === 'NaN:NaN'
                                          ? '00:00'
                                          : podcast.duration || '00:00'}
                                      </small>
                                    </p>
                                  </td>
                                  <td className="text-center w-25">{podcast.subtitle}</td>
                                  <td className="text-center">{podcast.artists}</td>
                                  <td className="text-center">
                                    {podcast.podcastCategory?.categoryName || 'N/A'}
                                  </td>
                                  <td className="text-center">
                                    {podcast?.isPublished ? 'Published' : 'Not Published'}
                                  </td>
                                  <td className="text-center">
                                    <Link
                                      className="actions-icons mx-2 text-primary"
                                      title="Edit"
                                      to={{
                                        pathname: `/podcast/${podcast._id}`,
                                        search: `?redirect_back=${encodeURIComponent(
                                          location.pathname + location.search
                                        )}`,
                                      }}
                                    >
                                      <img src={AppPics.EditSvg} alt="Edit" />
                                    </Link>
                                    <button
                                      className="actions-icons mx-2 text-danger"
                                      title="Delete"
                                      onClick={() => handleDelete(podcast)}
                                    >
                                      <img src={AppPics.DeleteSvg} alt="Delete" />
                                    </button>
                                    <Link
                                      to={{
                                        pathname: `/podcast/${podcast._id}/episodes`,
                                        search: `?redirect_back=${encodeURIComponent(
                                          location.pathname + location.search
                                        )}`,
                                      }}
                                      title="View Episodes"
                                    >
                                      <button>
                                        <img src={AppPics.EpisodePng} alt="View Episodes" />
                                      </button>
                                    </Link>
                                  </td>
                                </tr>
                              ))
                            ) : searchKeyword.trim() !== '' ? (
                              <tr>
                                <td colSpan={6} className="text-center py-4">
                                  No podcasts found for "{searchKeyword}"
                                </td>
                              </tr>
                            ) : (
                              <tr>
                                <td colSpan={6} className="text-center py-4">
                                  No podcasts available.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <TablePaginate
                      currentPage={page}
                      maxCount={total}
                      defaultLimit={params.limit}
                      setPage={handlePageChange}
                      handleChange={handleLimitChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PodcastList;
